import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  FileText, 
  BarChart3, 
  Play,
  Zap,
  FolderOpen,
  User,
  LogOut,
  Settings,
  ChevronDown,
  Brain,
  MessageSquare,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavGuard } from '../contexts/NavGuardContext';
import api from '../services/api';
import './TopNavbar.css';

const TopNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { proposalOutlineCritical, lessonPlanCritical } = useNavGuard();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [points, setPoints] = useState(null);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [lastDelta, setLastDelta] = useState(0);
  const [lastReason, setLastReason] = useState('');
  const [pointsPulse, setPointsPulse] = useState(false);
  const [showTravelingPoints, setShowTravelingPoints] = useState(false);
  const [showSparkle, setShowSparkle] = useState(false);
  const [pointsBeforeUpdate, setPointsBeforeUpdate] = useState(null);
  const [pendingPointsTotal, setPendingPointsTotal] = useState(null);
  const pointsWidgetRef = useRef(null);
  const modalCircleRef = useRef(null);

  const handleShowIntroVideo = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('propulse_show_intro_video'));
    }
  };

  const navItems = [
    // {
    //   path: '/proposal-generator',
    //   icon: FileText,
    //   label: 'Generator',
    //   description: 'Generate proposals'
    // },
    // {
    //   path: '/proposal-dashboard',
    //   icon: Zap,
    //   label: 'Generate Proposal',
    //   description: 'Dashboard generator'
    // },
    {
      path: '/proposal-outline',
      icon: FileText,
      label: 'Proposal Generator',
      description: 'Design proposal ingredients'
    },
    {
      path: '/lesson-plan',
      icon: BookOpen,
      label: 'Lesson Plan Generator',
      description: 'Generate and edit lesson plans'
    },
    {
      path: '/management',
      icon: FolderOpen,
      label: 'Management',
      description: 'Manage proposals, organizations, and teams'
    },
    {
      path: '/survey',
      icon: Brain,
      label: 'Survey',
      description: 'Take resilience prediction survey with EEG biofeedback'
    },
    // {
    //   path: '/chatbot',
    //   icon: MessageSquare,
    //   label: 'Chatbot',
    //   description: 'Ask questions about your proposals and documents'
    // },
    // {
    //   path: '/proposals',
    //   icon: FolderOpen,
    //   label: 'Proposals',
    //   description: 'View and edit proposals'
    // },
    // {
    //   path: '/lp',
    //   icon: FileText,
    //   label: 'Lesson Plans',
    //   description: 'Chat-based LP generator'
    // },
    // {
    //   path: '/analysis',
    //   icon: BarChart3,
    //   label: 'Analysis',
    //   description: 'View analysis data'
    // },
    // {
    //   path: '/analysis-runner',
    //   icon: Play,
    //   label: 'Analysis Runner',
    //   description: 'Run pipeline analysis'
    // },
    // {
    //   path: '/category-manager',
    //   icon: BarChart3,
    //   label: 'Categories',
    //   description: 'Manage proposal categories'
    // }
  ];

  const handleLogout = async () => {
    try {
      console.log('Logout clicked - starting logout process...');
      setIsUserMenuOpen(false); // Close the dropdown immediately
      await logout();
      console.log('Logout completed - navigating to login...');
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login even if logout API fails
      navigate('/login');
    }
  };

  const getUserDisplayName = () => {
    if (user?.profile?.first_name && user?.profile?.last_name) {
      return `${user.profile.first_name} ${user.profile.last_name}`;
    }
    return user?.username || user?.email || 'User';
  };

  const getUserInitials = () => {
    if (user?.profile?.first_name && user?.profile?.last_name) {
      return `${user.profile.first_name[0]}${user.profile.last_name[0]}`.toUpperCase();
    }
    if (user?.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    // Add event listeners when dropdown is open
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    // Cleanup event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isUserMenuOpen]);

  // Initialize points from gamification API
  useEffect(() => {
    const fetchPoints = async () => {
      try {
        const response = await api.get('/gamification/points/');
        const total = response?.data?.total_points ?? 0;
        setPoints(total);
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('propulse_last_points', String(total));
          } catch (e) {
            // ignore storage errors
          }
        }
      } catch (error) {
        // Silent fail – navbar points are a nice-to-have
        // console.error('Failed to fetch gamification points', error);
      }
    };

    if (user) {
      fetchPoints();
    }
  }, [user]);

  // Listen for global points update events from other parts of the app
  useEffect(() => {
    const handler = (event) => {
      console.log('[TopNavbar] Received propulse_points_update event:', event.detail);
      const detail = event.detail || {};
      const { delta, total, reason } = detail;
      console.log('[TopNavbar] Parsed:', { delta, total, reason });
      
      if (delta && delta !== 0) {
        console.log('[TopNavbar] ✅ Showing modal with delta:', delta);
        // Store current points before update - DON'T update points yet
        setPointsBeforeUpdate(points);
        // Store the new total to use after animation completes
        if (typeof total === 'number') {
          setPendingPointsTotal(total);
          if (typeof window !== 'undefined') {
            try {
              localStorage.setItem('propulse_pending_points', String(total));
            } catch (e) {
              // ignore
            }
          }
        }
        setLastDelta(delta);
        setLastReason(reason || 'Points earned');
        setShowPointsModal(true);
        setPointsPulse(true);
        // Keep pulse while modal is open (will be cleared when modal closes)
      } else {
        // No delta, just update points normally
        if (typeof total === 'number') {
          setPoints(total);
          if (typeof window !== 'undefined') {
            try {
              localStorage.setItem('propulse_last_points', String(total));
            } catch (e) {
              // ignore
            }
          }
        }
        console.log('[TopNavbar] ⚠️ Delta is 0 or falsy, NOT showing modal. delta:', delta);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('propulse_points_update', handler);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('propulse_points_update', handler);
      }
    };
  }, []);

  return (
    <>
      <nav className="top-navbar">
        <div className="navbar-container">
          {/* Logo */}
          <div className="navbar-brand">
            <Link to="/proposal-outline" className="brand-link">
              <Zap className="brand-icon" />
              <span className="brand-text">PropulseAI</span>
            </Link>
          </div>

          {/* Navigation Items */}
          <div className="navbar-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path + '/'));
              const onProposal = location.pathname.startsWith('/proposal-outline');
              const onLessonPlan = location.pathname.startsWith('/lesson-plan');
              const leavingProposal = onProposal && item.path !== '/proposal-outline' && !item.path.startsWith('/proposal-outline');
              const leavingLessonPlan = onLessonPlan && item.path !== '/lesson-plan' && !item.path.startsWith('/lesson-plan');
              const needConfirm = (leavingProposal && proposalOutlineCritical) || (leavingLessonPlan && lessonPlanCritical);
              const handleNavClick = (e) => {
                if (needConfirm && !window.confirm('Work is in progress. Leave anyway? You may lose unsaved progress.')) {
                  e.preventDefault();
                  return;
                }
                navigate(item.path);
              };
              if (needConfirm) {
                return (
                  <span
                    key={item.path}
                    role="link"
                    tabIndex={0}
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    title={item.description}
                    onClick={handleNavClick}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNavClick(e); } }}
                    style={{ cursor: 'pointer' }}
                  >
                    <Icon className="nav-icon" />
                    <span className="nav-label">{item.label}</span>
                  </span>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  title={item.description}
                >
                  <Icon className="nav-icon" />
                  <span className="nav-label">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* User + Points */}
          <div className="navbar-user">
            {/* Points Widget */}
            {points !== null && (
              <div 
                ref={pointsWidgetRef}
                className={`points-widget ${pointsPulse ? 'points-pulse' : ''} ${showSparkle ? 'points-sparkle' : ''}`} 
                title="Your points"
                onClick={() => navigate('/management?section=gamification')}
                style={{ cursor: 'pointer' }}
              >
                <div className="points-circle">
                  <span className={`points-number ${showSparkle ? 'points-number-update' : ''}`}>
                    {/* Show old points while modal is open, then new points after animation */}
                    {showPointsModal && pointsBeforeUpdate !== null ? pointsBeforeUpdate : points}
                  </span>
                </div>
                {showSparkle && (
                  <div className="sparkle-container">
                    <div className="sparkle sparkle-1"></div>
                    <div className="sparkle sparkle-2"></div>
                    <div className="sparkle sparkle-3"></div>
                    <div className="sparkle sparkle-4"></div>
                    <div className="sparkle sparkle-5"></div>
                    <div className="sparkle sparkle-6"></div>
                  </div>
                )}
              </div>
            )}

            {/* Help / intro video button */}
            <button
              type="button"
              className="intro-video-button"
              onClick={handleShowIntroVideo}
              title="Watch how to use the new Propulse"
            >
              <span className="intro-video-dot" />
              <span className="intro-video-label">VIDEO DEMO</span>
            </button>

            <div className="user-menu-container" ref={dropdownRef}>
              <button 
                className="user-menu-trigger"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              >
                <div className="user-avatar">
                  {user?.profile?.avatar_url ? (
                    <img 
                      src={user.profile.avatar_url} 
                      alt={getUserDisplayName()}
                      className="avatar-image"
                    />
                  ) : (
                    <span className="avatar-text">{getUserInitials()}</span>
                  )}
                </div>
                <div className="user-info">
                  <span className="user-name">{getUserDisplayName()}</span>
                  <span className="user-email">{user?.email}</span>
                </div>
                <ChevronDown className={`chevron-icon ${isUserMenuOpen ? 'open' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar">
                        {user?.profile?.avatar_url ? (
                          <img 
                            src={user.profile.avatar_url} 
                            alt={getUserDisplayName()}
                            className="dropdown-avatar-image"
                          />
                        ) : (
                          <span className="dropdown-avatar-text">{getUserInitials()}</span>
                        )}
                      </div>
                      <div className="dropdown-user-details">
                        <span className="dropdown-user-name">{getUserDisplayName()}</span>
                        <span className="dropdown-user-email">{user?.email}</span>
                        {user?.profile?.company && (
                          <span className="dropdown-user-company">{user.profile.company}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="dropdown-divider"></div>
                  
                  <div className="dropdown-items">
                    <button className="dropdown-item">
                      <User className="dropdown-icon" />
                      <span>Profile</span>
                    </button>
                    <button className="dropdown-item">
                      <Settings className="dropdown-icon" />
                      <span>Settings</span>
                    </button>
                  </div>
                  
                  <div className="dropdown-divider"></div>
                  
                  <div className="dropdown-items">
                    <button className="dropdown-item logout-item" onClick={handleLogout}>
                      <LogOut className="dropdown-icon" />
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Points Modal (no auto-dismiss) */}
      {showPointsModal && lastDelta !== 0 && (
        <div className="points-modal-overlay">
          {/* Fireworks/Graffiti animations */}
          <div className="fireworks-container">
            {[...Array(30)].map((_, i) => (
              <div key={i} className={`firework firework-${i + 1}`}></div>
            ))}
          </div>
          <div className="graffiti-bursts">
            {[...Array(12)].map((_, i) => (
              <div key={i} className={`graffiti-burst burst-${i + 1}`}></div>
            ))}
          </div>
          
          <div className="points-modal-card">
            <div className="points-modal-glow">
              <div ref={modalCircleRef} className="points-modal-circle">
                <span className="points-modal-delta">
                  {lastDelta > 0 ? `+${lastDelta}` : lastDelta}
                </span>
              </div>
            </div>
            <div className="points-modal-text">
              <div className="points-modal-title">Points earned</div>
              {lastReason && (
                <div className="points-modal-reason">{lastReason}</div>
              )}
            </div>
            <button
              type="button"
              className="points-modal-button"
              onClick={() => {
                // Trigger traveling animation
                if (modalCircleRef.current && pointsWidgetRef.current) {
                  const modalRect = modalCircleRef.current.getBoundingClientRect();
                  const widgetRect = pointsWidgetRef.current.getBoundingClientRect();
                  
                  // Calculate start and end positions
                  const startX = modalRect.left + modalRect.width / 2;
                  const startY = modalRect.top + modalRect.height / 2;
                  const endX = widgetRect.left + widgetRect.width / 2;
                  const endY = widgetRect.top + widgetRect.height / 2;
                  
                  // Set CSS custom properties for animation
                  document.documentElement.style.setProperty('--travel-start-x', `${startX}px`);
                  document.documentElement.style.setProperty('--travel-start-y', `${startY}px`);
                  document.documentElement.style.setProperty('--travel-end-x', `${endX}px`);
                  document.documentElement.style.setProperty('--travel-end-y', `${endY}px`);
                  
                  // Start traveling animation
                  setShowTravelingPoints(true);
                  
                  // Hide modal after a short delay (let animation start)
                  setTimeout(() => {
                    setShowPointsModal(false);
                    setPointsPulse(false); // Stop pulse when modal closes
                  }, 150);
                  
                  // Show sparkle and update points when animation reaches navbar
                  setTimeout(() => {
                    setShowTravelingPoints(false);
                    setShowSparkle(true);
                    // Update points NOW (after traveling animation completes)
                    // Use localStorage as source of truth to avoid stale closure issues
                    if (typeof window !== 'undefined') {
                      try {
                        const pendingPoints = localStorage.getItem('propulse_pending_points');
                        if (pendingPoints) {
                          const newPoints = parseInt(pendingPoints, 10);
                          setPoints(newPoints);
                          setPendingPointsTotal(null); // Clear state
                          localStorage.setItem('propulse_last_points', String(newPoints));
                          localStorage.removeItem('propulse_pending_points');
                        }
                      } catch (e) {
                        console.warn('Failed to update points after animation:', e);
                      }
                    }
                    // Hide sparkle after animation
                    setTimeout(() => {
                      setShowSparkle(false);
                    }, 1000);
                  }, 850);
                } else {
                  setShowPointsModal(false);
                  setPointsPulse(false);
                }
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      {/* Traveling points animation - outside modal so it persists */}
      {showTravelingPoints && lastDelta !== 0 && (
        <div className="traveling-points">
          <div className="traveling-points-circle">
            <span className="traveling-points-delta">
              {lastDelta > 0 ? `+${lastDelta}` : lastDelta}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default TopNavbar;